import { OnlyViewHoc } from 'hocs'
import React from 'react'

import { getFio } from 'helpers/formatText'
import { useGetSupervisorsQuery } from 'redux/services/common'
import { ApplicationParams } from 'services/api/ApplicaitionApi'

import { AppButton } from 'components/AppButton'
import AppCkeckbox from 'components/AppCkeckbox'
import AppDatePicker from 'components/AppDatePicker/AppDatePicker'
import { AppFiltersLayout } from 'components/AppFiltersLayout'
import AppRadio from 'components/AppRadio'
import { AppSelect } from 'components/AppSelect'
import { SelectMulti, SelectMultiOption } from 'components/SelectMulti'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH, TextFieldPhone } from 'components/TextFieldPhone'

import { Branch } from 'models/branch'
import { Region } from 'models/region'
import { Status } from 'models/status'

interface Props {
  regions: Region[]
  statuses: Status[]
  branches: Branch[]
  onSubmit?(values: ApplicationParams): void
  onDownloadExcel(values: ApplicationParams): void
  onDownloadExcelBonus(values: ApplicationParams): void
  downloadApplicationsForMonth(values: ApplicationParams): void
  downloadingExel: boolean
  downloadingExelBonus: boolean
  downloadingExelForMonth: boolean
}

const initialParams: ApplicationParams = {
  status: '',
  region_id: '',
  start_date: '',
  end_date: '',
  region: '',
  consultant: '',
  branch: '',
  pan: '',
  inn: '',
  fio: '',
  supervisor: '',
  use_verification_date: false,
}

const defaultListValue = {
  title: 'Все',
  value: '',
}
const cardTypeOptions: SelectMultiOption[] = [
  { label: 'MJunior', value: 'm_junior' },
  { label: 'Mislamik', value: 'p_islamic' },
  { label: 'Элкарт', value: 'el_kart' },
  { label: 'MasterCard', value: 'master_card' },
  { label: 'Messi MasterCard', value: 'messi_master_card' },
  { label: 'Messi MJunior', value: 'messi_m_junior' },
]

export const ApplicationDetailsHeader: React.FC<Props> = ({
  statuses,
  regions,
  branches,
  onSubmit,
  onDownloadExcel,
  onDownloadExcelBonus,
  downloadingExel,
  downloadingExelBonus,
  downloadApplicationsForMonth,
  downloadingExelForMonth,
}) => {
  const [params, setParams] = React.useState<ApplicationParams>(initialParams)
  const { data: supervisors } = useGetSupervisorsQuery()

  const regionList = regions.map(item => ({
    title: item.name,
    value: item.id,
  }))

  const statusList = statuses.map(item => ({
    title: item.name,
    value: item.code,
  }))
  const branchList = (
    params.region_id
      ? branches.filter(branch =>
          branch.region_id
            ? branch.region_id === Number(params.region_id)
            : true,
        )
      : branches
  ).map(item => ({
    title: item.name,
    value: item.id,
  }))

  function _submit() {
    const _params = {
      ...params,
      supervisor:
        params.supervisor?.length === KG_PHONE_MAX_LENGTH
          ? params.supervisor
          : '',
    }
    onSubmit?.(_params)
  }

  function _reset() {
    setParams(initialParams)
    onSubmit?.(initialParams)
  }

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    setParams(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  function setParamByName(
    name: keyof ApplicationParams,
    value?: string | number | boolean,
  ) {
    setParams({
      ...params,
      [name]: value,
    })
  }

  const selectedSupervisorText = getFio(
    supervisors?.results?.find(s => s.phone === params.supervisor),
  )

  const selectedCardTypes: SelectMultiOption[] = cardTypeOptions.filter(
    option => Boolean(params[option.value as keyof ApplicationParams]),
  )

  function handleCardGroupChange(value: ApplicationParams['card_group']) {
    setParams(prev => {
      const next = { ...prev, card_group: value }
      for (const option of cardTypeOptions) {
        delete next[option.value as keyof typeof next]
      }
      return next
    })
  }

  function handleCardTypesChange(selected: SelectMultiOption[]) {
    const selectedValues = new Set(selected.map(option => option.value))

    setParams(prev => {
      const next = { ...prev }

      if (selected.length > 0) {
        delete next.card_group
      }

      for (const option of cardTypeOptions) {
        if (selectedValues.has(option.value)) {
          next[option.value as 'p_islamic'] = true
        } else {
          delete next[option.value as keyof typeof next]
        }
      }

      return next
    })
  }

  return (
    <AppFiltersLayout
      controls={
        <>
          <AppButton size="small" onClick={_submit}>
            Применить
          </AppButton>
          <AppButton size="small" variant="red" onClick={_reset}>
            Сбросить
          </AppButton>
          <OnlyViewHoc>
            <AppButton
              onClick={() => onDownloadExcel(params)}
              disabled={downloadingExel}
              variant="siren_dashed_border"
              size="small"
            >
              Сгенерировать отчет
            </AppButton>
            <AppButton
              onClick={() => onDownloadExcelBonus(params)}
              disabled={downloadingExelBonus}
              variant="siren_dashed_border"
              size="small"
            >
              Скачать Excel Bonus
            </AppButton>
            <AppButton
              onClick={() => downloadApplicationsForMonth(params)}
              disabled={downloadingExelForMonth}
              variant="siren_dashed_border"
              size="small"
            >
              Скачать Excel за месяц
            </AppButton>
          </OnlyViewHoc>
        </>
      }
    >
      <AppSelect
        size="extra_small"
        text={
          regionList.find(item => item.value === Number(params.region_id))
            ?.title
        }
        onChangeValues={value => setParamByName('region_id', value?.[0] || '')}
        list={[defaultListValue, ...regionList]}
        placeholder="Все"
        label="Регион"
      />

      <AppSelect
        size="extra_small"
        text={
          branchList.find(item => item.value === Number(params.branch))?.title
        }
        onChangeValues={value => setParamByName('branch', value?.[0] || '')}
        list={[defaultListValue, ...branchList]}
        placeholder="Все"
        label="Филиал"
      />
      <TextFieldPhone
        inputSize="small"
        onChange={_handleChange}
        onChangePhone={value => setParamByName('consultant', value.phone)}
        value={params.consultant || ''}
        name="consultant"
        label="Консультант"
        placeholder="Введите консультанта"
      />
      <TextField
        inputSize="small"
        type="number"
        onChange={_handleChange}
        value={params.pan}
        name="pan"
        label="ПАН"
        placeholder="Последние 7 цифр"
      />
      <AppSelect
        size="extra_small"
        text={statusList.find(item => item.value === params.status)?.title}
        onChangeValues={value => setParamByName('status', value?.[0] || '')}
        list={[defaultListValue, ...statusList]}
        placeholder="Все"
        label="Статус"
      />
      <AppCkeckbox
        title=" "
        label="По дате верификации"
        checked={params.use_verification_date}
        onChange={checked => {
          if (checked) {
            setParams({
              ...params,
              status: 'success',
              use_verification_date: checked,
            })
          } else {
            setParams({
              ...params,
              use_verification_date: checked,
            })
          }
        }}
      />
      <TextField
        inputSize="small"
        onChange={_handleChange}
        value={params.fio}
        name="fio"
        label="ФИО"
        placeholder="Фамилия Имя Отчество"
      />
      <TextField
        inputSize="small"
        type="number"
        onChange={_handleChange}
        value={params.inn}
        name="inn"
        label="ИНН"
        placeholder="Введите ИНН"
      />
      <AppSelect
        size="extra_small"
        label="Выберите супервайзера"
        text={selectedSupervisorText}
        onChangeValues={([selectedValue]) => {
          setParamByName('supervisor', selectedValue)
        }}
        list={[
          defaultListValue,
          ...(supervisors?.results || []).map(item => ({
            value: item.phone,
            title: getFio(item),
          })),
        ]}
      />
      <SelectMulti
        label="Тип карты"
        placeholder="Выберите тип карты"
        options={cardTypeOptions}
        value={selectedCardTypes}
        onChange={handleCardTypesChange}
      />
      <AppDatePicker
        label="Начало"
        inputSize="small"
        value={params.start_date}
        onchange={date => {
          setParamByName('start_date', date)
        }}
      />
      <AppDatePicker
        label="Конец"
        inputSize="small"
        value={params.end_date}
        onchange={date => {
          setParamByName('end_date', date)
        }}
      />
      <AppRadio
        title="Группа карт"
        name="card_group"
        value={params.card_group}
        options={[
          {
            label: 'Джуниор',
            value: 'junior',
          },
          {
            label: 'Стандарт',
            value: 'standard',
          },
        ]}
        onChange={handleCardGroupChange}
      />
    </AppFiltersLayout>
  )
}
